import { FormEvent, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { useCartStore } from '@lib/store/cartStore';
import { useAuth } from '@hooks/useAuth';
import { formatCurrency } from '@lib/utils';
import { ROUTES } from '@lib/routes';
import { useFormFields } from '@hooks/useFormFields';
import { validateWithSchema } from '@lib/forms';

const checkoutSchema = z.object({
  fullName: z.string().min(2, 'Full name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  address: z.string().min(5, 'Address is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code is required'),
  country: z.string().min(2, 'Country is required'),
});

type CheckoutFormData = z.infer<typeof checkoutSchema>;

export function CheckoutPage(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const items = useCartStore((state) => state.items);
  const getTotal = useCartStore((state) => state.getTotal);
  const clearCart = useCartStore((state) => state.clearCart);
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, { state: { from: ROUTES.CUSTOMER.CHECKOUT } });
    }
  }, [isAuthenticated, navigate]);

  const { values, handleChange, errors: fieldErrors, setErrors } = useFormFields<CheckoutFormData>({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  const total = getTotal();

  if (items.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
                Your cart is empty
              </p>
              <Link to={ROUTES.CUSTOMER.CART}>
                <Button>Back to Cart</Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const validation = validateWithSchema(checkoutSchema, values);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }

    setIsProcessing(true);
    try {
      // TODO: Process payment with Stripe
      // TODO: Create order in Firestore
      
      // Simulate payment processing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      clearCart();
      navigate(ROUTES.CUSTOMER.PRODUCTS, {
        state: { orderSuccess: true },
      });
    } catch (error) {
      console.error('Checkout failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleFormSubmit}>
              <Card className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Shipping Information
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Full Name"
                    name="fullName"
                    value={values.fullName}
                    onChange={handleChange}
                    error={fieldErrors.fullName}
                  />
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    error={fieldErrors.email}
                  />
                  <Input
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={values.phone}
                    onChange={handleChange}
                    error={fieldErrors.phone}
                  />
                  <Input
                    label="Address"
                    name="address"
                    value={values.address}
                    onChange={handleChange}
                    error={fieldErrors.address}
                  />
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input
                      label="City"
                      name="city"
                      value={values.city}
                      onChange={handleChange}
                      error={fieldErrors.city}
                    />
                    <Input
                      label="State"
                      name="state"
                      value={values.state}
                      onChange={handleChange}
                      error={fieldErrors.state}
                    />
                    <Input
                      label="ZIP Code"
                      name="zipCode"
                      value={values.zipCode}
                      onChange={handleChange}
                      error={fieldErrors.zipCode}
                    />
                  </div>
                  <Input
                    label="Country"
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    error={fieldErrors.country}
                  />
                </div>
              </Card>

              <Card>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Payment Information
                </h2>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Payment processing will be integrated with Stripe. For now, this is a
                    placeholder.
                  </p>
                </div>
              </Card>

              <div className="mt-6 flex gap-4">
                <Link to={ROUTES.CUSTOMER.CART} className="flex-1">
                  <Button variant="outline" fullWidth>
                    Back to Cart
                  </Button>
                </Link>
                <Button type="submit" fullWidth isLoading={isProcessing}>
                  Complete Order
                </Button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Order Summary
              </h2>
              <div className="space-y-3 mb-4">
                {items.map((item) => (
                  <div key={item.productId} className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

